import { useEffect, useRef, useState } from "react";
import { Users, Crosshair, Heart, ChevronRight, RotateCcw, Skull, Zap, Package, Radio } from "lucide-react";
import { RoadEngine } from "./roadEngine";
import { SpaceHomeBtn } from "./SpaceNav";
import { loadState, executeBunkerFallout } from "./stateManager";
import { sfx } from "./audio";
import { spaceApi } from "./spaceApi";

const BASE_BGM = "/assets/vault/audio/command_deck_theme.mp3";

const R = "/game/road";
const B = "/game/bunker";
const HERO_SPRITES = { jess: `${R}/hero_jess.png`, bazooka: `${R}/hero_bazooka.png`, nancy: `${R}/hero_nancy.png` };
const BUNKER_BGS = { 1: `${B}/bg_bunker.jpg`, 2: `${B}/bg_bunker.jpg`, 3: `${B}/bg_bunker_boss.jpg` };

// Crash-landing comic intro.
const PANELS = [
  { img: `${B}/panel_crash.jpg`, text: "Emergency descent. Escape Pod 7 breaches the atmosphere over the Dust Reaches — engines gone, hull screaming." },
  { img: `${B}/panel_survivors.jpg`, text: "One walks from the wreck alone: Jess, a Vanguard Officer — Level 1, one sidearm, and a long road ahead." },
  { img: `${B}/panel_descent.jpg`, text: "A blast hatch sits in the canyon floor — the only shelter for miles. She fights toward it, gathering allies along the way." },
  { img: `${B}/panel_boss.jpg`, text: "But something ancient stirs beneath the hatch… and it has been waiting a very long time." },
];

// The 5-part Last Z survival prologue beat map (surfaced as HUD banners).
const PARTS = {
  1: { title: "PART 1 · HIGHWAY SOLO", sub: "Jess · Lv 1 · Basic Pistol — evade debris, pick off Skitters", Icon: Crosshair, color: "#ff8a5c" },
  2: { title: "PART 2 · ASSAULT RIFLE ACQUIRED", sub: "Firepower crate breached — spray fire · -60% cooldown", Icon: Package, color: "#f5c542" },
  3: { title: "PART 3 · REINFORCEMENTS", sub: "Drop-pod deployed — 3 troopers shield the line", Icon: Radio, color: "#4fc3ff" },
  4: { title: "PART 4 · NURSE NANCY JOINS", sub: "Medic online — heal-pulse restoring the squad", Icon: Heart, color: "#3fd6d0" },
  5: { title: "PART 5 · WARLORD · GI BOZOK JOINS", sub: "The heavy drops in — end the Warlord", Icon: Skull, color: "#ff6a6a" },
};

const SECTOR_NAME = { 1: "Highway · Dust Reaches", 2: "Perimeter · Compound Gate", 3: "Bunker Hatch · Arena" };
const INTER = {
  1: { title: "HIGHWAY CLEARED", area: "APPROACH · COMPOUND PERIMETER", body: "The open road's behind you. The safety compound gate is ahead — but the swarm thickens.", cta: "Advance" },
  2: { title: "PERIMETER BREACHED", area: "WARNING · BUNKER HATCH AHEAD", body: "The hatch arena is close. Whatever nests below is awake. Weapons hot.", cta: "Breach the Hatch" },
};

export default function StoryCampaign({ onBack }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [phase, setPhase] = useState("intro"); // intro | playing | interstitial | rescue | kidnap | continued | gameover
  const [panel, setPanel] = useState(0);
  const [fromStage, setFromStage] = useState(1);
  const [hud, setHud] = useState({ troops: 0, weaponTier: 1, lives: 3, stage: 1, progress: 0, synergy: false });
  const [encounter, setEncounter] = useState(false);
  const [started, setStarted] = useState(false);
  const [runId, setRunId] = useState(0);
  const [part, setPart] = useState(null); // active part banner
  const partTimer = useRef(null);

  const flashPart = (n) => {
    setPart(n);
    if (partTimer.current) clearTimeout(partTimer.current);
    partTimer.current = setTimeout(() => setPart(null), 3200);
  };

  // Mount the engine only once the intro is done (canvas exists in DOM by then).
  useEffect(() => {
    if (!started) return;
    setHud({ troops: 0, weaponTier: 1, lives: 3, stage: 1, progress: 0, synergy: false });
    setEncounter(false);
    flashPart(1);
    const eng = new RoadEngine(canvasRef.current, {
      campaign: true,
      initialRoster: ["jess"],            // Part 1 — Jess solo at Level 1
      enableRescue: true,                 // Part 4 — Nancy rescued mid-perimeter
      startWeapon: 1,                     // basic pistol
      stageLen: 760,
      crateAt: 0.5, crateWeaponLevel: 3,  // Part 2 — mid-stage-1 assault-rifle crate
      dropPodTroops: 3,                   // Part 3 — reinforcements on stage 2
      giBozokAtBoss: true,                // Part 5 — Gi Bozok drop-in at the boss
      healPulse: true,                    // Nancy's passive heal tick
      troopShield: true,                  // troops absorb hits before Jess
      heroSprites: HERO_SPRITES, bossSprite: `${B}/boss_warlord.png`, stageBackdrops: BUNKER_BGS,
      bossName: "ALIEN WARLORD", bossHp: 55, rescueSprite: `${B}/med_bay.png`,
      onUpdate: (s) => setHud(s),
      onStageClear: (stage) => { setFromStage(stage); setPhase("interstitial"); },
      onCrate: () => flashPart(2),
      onDropPod: () => flashPart(3),
      onRescueEncounter: () => setEncounter(true),
      onRescue: () => { setEncounter(false); setPhase("rescue"); },
      onGiBozok: () => flashPart(5),
      onFinish: () => { sfx.victory(); executeBunkerFallout(loadState()); setPhase("kidnap"); },
      onGameOver: () => { sfx.defeat(); setPhase("gameover"); },
    });
    engineRef.current = eng;
    eng.mount();
    spaceApi.gameAssets().then((a) => sfx.startBgm(a.combat_theme || a.mist_theme || a.road_patrol_theme)).catch(() => {});
    const onResize = () => eng.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); if (partTimer.current) clearTimeout(partTimer.current); eng.unmount(); sfx.startBgm(BASE_BGM); };
  }, [started, runId]);

  const nextPanel = () => {
    if (panel < PANELS.length - 1) setPanel((p) => p + 1);
    else { setPhase("playing"); setStarted(true); }
  };
  const advance = () => { engineRef.current?.advanceStage(); setPhase("playing"); };
  const deployNancy = () => { engineRef.current?.completeRescue(); flashPart(4); setPhase("playing"); };
  const replay = () => { setPhase("intro"); setPanel(0); setStarted(false); setRunId((n) => n + 1); };

  const inter = INTER[fromStage];
  const p = PANELS[panel];
  const pb = part ? PARTS[part] : null;

  return (
    <div className="ss-viewport rp-viewport" data-testid="story-campaign-app">
      <SpaceHomeBtn onClick={onBack} testid="story-back" />

      {/* Comic intro */}
      {phase === "intro" && (
        <div className="story-panel" data-testid="story-intro" onClick={nextPanel}>
          <img src={p.img} alt="" className="story-panel-img" />
          <div className="story-panel-fade" />
          <div className="story-panel-cap">
            <p data-testid="story-panel-text">{p.text}</p>
            <div className="story-panel-foot">
              <span className="story-dots">{PANELS.map((_, i) => <i key={i} className={i === panel ? "on" : ""} />)}</span>
              <button className="story-tap" data-testid="story-panel-next" onClick={(e) => { e.stopPropagation(); nextPanel(); }}>
                {panel < PANELS.length - 1 ? "Tap to continue" : "Begin the run"} <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gameplay canvas (mounted once intro finishes) */}
      {started && <canvas ref={canvasRef} className="ss-canvas" data-testid="story-canvas" />}

      {started && (
        <div className="rp-hud" data-testid="story-hud">
          <div className="rp-stat"><b>S{hud.stage}</b><span>{SECTOR_NAME[hud.stage]}</span></div>
          <div className="rp-stat"><Users size={14} /> <b>{hud.troops}</b><span>buffer</span></div>
          <div className="rp-stat wpn"><Crosshair size={14} /> T<b>{hud.weaponTier}</b></div>
          <div className="rp-lives" data-testid="story-lives">
            {[0, 1, 2].map((i) => <Heart key={i} size={15} fill={i < hud.lives ? "#ff4560" : "none"} stroke={i < hud.lives ? "#ff4560" : "#5b6b82"} />)}
          </div>
          <div className="rp-progress"><span style={{ width: `${hud.progress * 100}%` }} /></div>
          {hud.synergy && <div className="rp-synergy" data-testid="story-synergy">⚡ TRI-FACTION +15% ATK / +15% DEF</div>}
        </div>
      )}

      {/* 5-part beat banner */}
      {started && pb && (
        <div data-testid={`story-part-${part}`} style={{
          position: "absolute", left: "50%", top: "22%", transform: "translateX(-50%)", zIndex: 30,
          background: "rgba(6,10,20,0.82)", border: `1px solid ${pb.color}`, borderRadius: 14, padding: "10px 16px",
          textAlign: "center", boxShadow: `0 0 26px ${pb.color}55`, backdropFilter: "blur(6px)", maxWidth: "86%",
          animation: "mistPartIn 0.3s ease-out", pointerEvents: "none",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, color: pb.color, fontWeight: 900, fontSize: 13, letterSpacing: 0.6 }}>
            <pb.Icon size={15} /> {pb.title}
          </div>
          <div style={{ color: "#cfe0f5", fontSize: 11, marginTop: 3 }}>{pb.sub}</div>
        </div>
      )}

      {phase === "playing" && started && !encounter && !pb && (
        <div className="rp-hint" data-testid="story-hint">Drag to steer · flip red gates · troops shield you · protect the survivors</div>
      )}

      {/* Perimeter medbay rescue encounter banner */}
      {phase === "playing" && encounter && (
        <div className="rp-encounter" data-testid="story-encounter">
          <span>🚑 SURVIVOR IN THE MEDBAY</span>
          <small>Clear the alien swarm to breach the door</small>
        </div>
      )}

      {/* Sector interstitial */}
      {phase === "interstitial" && inter && (
        <div className="ss-overlay rp-inter" data-testid="story-interstitial">
          <span className="rp-inter-flag">🚩 {inter.title}</span>
          <span className="rp-inter-area">{inter.area}</span>
          <div className="rp-inter-card"><p>{inter.body}</p></div>
          <button className="ss-enter-btn" data-testid="story-interstitial-advance" onClick={advance}>{inter.cta} <ChevronRight size={16} /></button>
        </div>
      )}

      {/* Nancy rescue — door breach */}
      {phase === "rescue" && (
        <div className="ss-overlay rp-rescue" data-testid="story-rescue">
          <span className="rp-rescue-siren">🚪 DOOR BREACHED</span>
          <span className="ov-t" style={{ color: "#4fc3ff" }}>NURSE NANCY · Level 1 · Blue Tier</span>
          <span className="ov-s">Pinned inside the boarded ambulance — now free. She joins the fireteam, activates the dual-layout HUD, and begins a periodic <b>heal-pulse</b> across the squad, locking in the <b>+15% ATK / +15% DEF</b> tri-faction synergy.</span>
          <button className="ss-enter-btn" data-testid="story-rescue-deploy" onClick={deployNancy} style={{ borderColor: "rgba(79,195,255,0.7)", background: "rgba(79,195,255,0.16)", color: "#bfe6ff" }}>
            <Zap size={16} /> Bring Nancy In
          </button>
        </div>
      )}

      {/* Kidnap cutscene — fallout already persisted on boss defeat */}
      {phase === "kidnap" && (
        <div className="story-panel kidnap" data-testid="story-kidnap">
          <img src={`${B}/panel_boss.jpg`} alt="" className="story-panel-img" />
          <div className="story-panel-fade red" />
          <div className="story-panel-cap">
            <span className="kidnap-title"><Skull size={18} /> THE WARLORD STRIKES</span>
            <p>In its death-throes a Zornelex Vanguard lunges — and drags <b>Gi Bozok</b> screaming into the shadows. The entry grid collapses. Jess and Nancy's power crashes to <b>Level 1</b> as they fall into the bunker below.</p>
            <button className="ss-enter-btn danger" data-testid="story-kidnap-continue" onClick={() => setPhase("continued")}>Continue <ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* To be continued */}
      {phase === "continued" && (
        <div className="ss-overlay tbc" data-testid="story-continued">
          <span className="tbc-t">TO BE CONTINUED</span>
          <span className="tbc-s">Rebuild in the Bunker Hub. Clear debris for scrap, upgrade the Command Centre, and rescue Gi Bozok deep in Sector 45… and rise.</span>
          <button className="ss-enter-btn" data-testid="story-continued-back" onClick={onBack}>Enter the Bunker Hub</button>
        </div>
      )}

      {/* Game over */}
      {phase === "gameover" && (
        <div className="ss-overlay lose" data-testid="story-gameover">
          <span className="ov-t">Jess Fell</span>
          <span className="ov-s">The run claimed the survivors. Regroup and run it back.</span>
          <button className="ss-enter-btn" data-testid="story-replay" onClick={replay}><RotateCcw size={16} /> Retry</button>
        </div>
      )}
    </div>
  );
}
